import * as d3 from 'd3-selection'
import symbols from './symbols'
import * as colors from 'colors'

const tilt = .25
const fillOffset = 10 // Spacing between the container and the fills
const padding = 20
const minLabelFontSize = 10
const minSeriesHeightForLabel = 18

const fills = (selection, diameter, chart, defs) => {
  let labels = chart.datum().axes.infographic.value

  selection.each(function (d) {
    let container = d3.select(this)
      , fill = container.selectAll('g.beaker-fill')
                        .data(d => {
                          return d.points.map(point => {
                            let scale = d.scale

                            return {
                              chartId: chart.id,
                              deemphasize: point.deemphasize,
                              clickable: point.clickable,
                              block: d.block,
                              color: point.color,
                              y0: scale(point._range[0]),
                              y1: scale(point._range[1]),
                              diameter,
                              point,
                              ry: (diameter / 2) * tilt
                            }
                          })
                        })
      , fillEnter = fill.enter()
                          .append('g')
                          .attr('class', 'beaker-fill is-deemphasizable')
                          .on('click', (e, d) => {
                            if (d.clickable) {
                              chart.ee.emit('chart-event', 'select:point', d.point, d.block.range)
                            }
                          })

    fill.exit().remove()

    fill = fill.merge(fillEnter)
               .attr('transform', `translate(${fillOffset}, 0)`)
               .classed('deemphasize', d => d.deemphasize)
               .classed('is-clickable', d => d.clickable)

    let path = fill.selectAll('path')
                   .data(d => {
                     let body = `M 0,${d.y0}
                                 a ${d.diameter / 2},${d.ry} 0,0,0 ${d.diameter} 0
                                 l 0, ${d.y1 - d.y0}
                                 a ${-d.diameter / 2},${d.ry} 0,0,0 ${-d.diameter} 0`
                       , top = `M ${d.diameter},${d.y1}
                                a ${d.diameter / 2},${d.ry} 0,0,0 ${-d.diameter} 0
                                a ${d.diameter / 2},${d.ry} 0,0,0 ${d.diameter} 0`

                     return [{
                      clazz: 'body',
                      fill: `url(#${d.chartId}-beaker-colored-gradient-body-a-${d.color})`,
                      path: body
                     }, {
                      clazz: 'body',
                      fill: `url(#${d.chartId}-beaker-colored-gradient-body-b-${d.color})`,
                      path: body
                     }, {
                      clazz: 'top',
                      fill: `url(#${d.chartId}-beaker-colored-gradient-top-${d.color})`,
                      path: top
                     }, {
                      clazz: 'top top-highlight',
                      fill: `url(#${d.chartId}-beaker-colored-gradient-top-highlight-${d.color})`,
                      clip: `${d.chartId}-beaker-colored-gradient-top-highlight-${d.color}-clip`,
                      filter: `url(#${d.chartId}-beaker-top-highlight-filter)`,
                      path: top
                     }]
                   })

    path.enter()
        .append('path')
        .attr('class', d => d.clazz)
        .attr('filter', d => d.filter)
        .merge(fill.selectAll('path'))
        .attr('fill', d => d.fill)
        .attr('clip-path', d => d.clip ? `url(#${d.clip})` : '')
        .attr('d', d => d.path)
        .filter(d => d.clip)
        .select(d => {
          let clip = defs.selectAll(`clipPath#${d.clip}`)
                         .data([d])
                         .enter()
                         .append('clipPath')
                           .attr('id', d.clip)
          clip.append('path')

          clip.merge(defs.selectAll(`clipPath#${d.clip}`))
              .selectAll('path')
              .attr('d', d.path)
        })


    // Determine the font size of labels
    let fontSize = Math.max((d.width / 25) + 12, minLabelFontSize) // font size based on width
      , multiple = d.points.length > 1

    if (multiple) {
      // If there are multiple series, we can only put the labels on the sides of the
      // series, so we also have to pay attention to how much height we have to draw
      // the labels.

      // Find the smallest font we're going to use that's above the min font size
      let heights = fill.data().map(fill => {
        return fill.y0 - fill.y1
      }).sort((a, b) => a - b)

      // Ignore series that are shorter than minSeriesHeightForLabel
      let minHeight = heights.find(height => {
        return height > minSeriesHeightForLabel
      })

      // Use the height-based font size if it's less than the ideal width-based font size.
      fontSize = Math.min(minHeight - 6, fontSize)
    }

    let label = fill.selectAll('g.text-group-label')
                    .data(d => labels ? [d] : [])
                    .join('g')
                    .attr('class', 'text-group-label')
                     .selectAll('text.label')
                      .data(d => [d])

    label.exit().remove()

    label.enter()
         .append('text')
           .attr('class', 'label label-on-colored-background')
           .merge(label)
           .classed('is-hidden', false) // show so we can grab proper dimensions
           .classed('light dark', false)
           .select(function (d) {
             d3.select(this)
               .classed(colors.getTextVariation(colors.lookup(d.color)), true)
             return this
           })
           .text(d => d.point.formatter(d.point.raw))
           .attr('font-size', fontSize)
           .classed('is-hidden', function (d) {
             if (!multiple) {
               return false
             }
             // Hide multi series if we don't have the space
             return (d.y0 - d.y1) < minSeriesHeightForLabel
           })

      // Position the labels
      fill.selectAll('g.text-group-label')
           .attr('transform', function (d) {
            let x = d.diameter / 2
              , height = d.y0 - d.y1
              , y = d.y0 - (height / 2) + d.ry // By default, center it within the body

            if (this.getBBox().height > height && !multiple) {
              // We can't fit the text into the body, center in the lid
              // but only when we're showing a single series.
              y = d.y1
            }

            return `translate(${x}, ${y})`
           })
  })
}

export default (selection, chart, defs, axisWidth) => {
  selection.each(function (d) {
    let beakerWidth = d.width - axisWidth - padding
      , diameter = beakerWidth - fillOffset * 2
      , container = d3.select(this)
                      .selectAll('.beaker-container')
                      .data(d => [d])
      , enter = container.enter()
                         .append('g')
                           .attr('class', 'beaker-container')

    defs.call(symbols, d.chartId, d.points, beakerWidth, d.height, d.ry)

    container = container.merge(enter)
                         .attr('transform', `translate(${padding}, 0)`)

    enter.append('use')
         .attr('href', `#${d.chartId}-beaker-background`)

    container.call(fills, diameter, chart, defs)

    enter.append('use')
         .attr('class', 'beaker-foreground')
         .attr('href', `#${d.chartId}-beaker-foreground`)


    // Add the bottom title
    let title = container.selectAll('g.text-group-title')
                         .data(d => d.title ? [d] : [])
                         .join('g')
                         .attr('class', 'text-group-title')
                         .attr('transform', function (d) {
                          let dimensions = d.block.dimensions
                            , center = (diameter / 2) + fillOffset
                            , width = this.getBBox().width
                            , left = center - width / 2

                          // If the text is too far left of the y-axis, align the center
                          // in a position so the left side of the text is aligned w/ the
                          // y-axis
                          if (left < 0 && axisWidth + left < 0) {
                            center = axisWidth - left
                          }

                          return `translate(${center}, ${dimensions.height - d.ry})`
                        })
                        .selectAll('text.title')
                        .data(d => [d])

    title.exit().remove()

    title = title.enter()
                 .append('text')
                 .attr('class', 'title')
                 .merge(title)

    title.text(d => d.title)

    container.selectAll('.beaker-foreground').raise()

  })
}

export { tilt }
